import React, { createContext, useContext, useState, useCallback, useRef, PropsWithChildren } from 'react';
import { tripsService, Trip, CreateTripInput, UpdateTripInput } from '../services/supabase';
import { useAuth } from './AuthContext';

interface TripsContextType {
  trips: Trip[];
  currentTrip: Trip | null;
  loading: boolean;
  error: string | null;
  fetchUserTrips: () => Promise<void>;
  fetchTripById: (tripId: string) => Promise<void>;
  createTrip: (input: CreateTripInput) => Promise<Trip>;
  updateTrip: (tripId: string, input: UpdateTripInput) => Promise<Trip>;
  deleteTrip: (tripId: string) => Promise<void>;
  setCurrentTrip: (trip: Trip | null) => void;
  getLastTripId: () => string | null;
}

const TripsContext = createContext<TripsContextType | undefined>(undefined);

const LAST_TRIP_ID_KEY = 'lastTripId';

export const TripsProvider: React.FC<PropsWithChildren> = ({ children }) => {
  const { firebaseUser } = useAuth();
  const [trips, setTrips] = useState<Trip[]>([]);
  const [currentTrip, setCurrentTripState] = useState<Trip | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Ref to track currentTrip without causing dependency changes
  const currentTripRef = useRef<Trip | null>(null);

  // Wrapper to save last trip ID to localStorage
  const setCurrentTrip = useCallback((trip: Trip | null) => {
    setCurrentTripState(trip);
    currentTripRef.current = trip;
    if (trip?.short_id) {
      localStorage.setItem(LAST_TRIP_ID_KEY, trip.short_id);
    }
  }, []);

  // Get last trip ID from localStorage or current trip
  // Uses ref to avoid dependency on currentTrip (which would cause infinite loops in DashboardLayout)
  const getLastTripId = useCallback((): string | null => {
    if (currentTripRef.current?.short_id) {
      return currentTripRef.current.short_id;
    }
    return localStorage.getItem(LAST_TRIP_ID_KEY);
  }, []);

  const fetchUserTrips = useCallback(async () => {
    if (!firebaseUser) {
      setTrips([]);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const data = await tripsService.getUserTrips(firebaseUser.uid);
      setTrips(data);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch trips';
      setError(errorMessage);
      console.error('Error fetching trips:', err);
    } finally {
      setLoading(false);
    }
  }, [firebaseUser]);

  const fetchTripById = useCallback(async (tripIdOrShortId: string) => {
    setLoading(true);
    setError(null);

    try {
      let trip: Trip | null = null;

      // If it looks like a short ID (6 chars, alphanumeric), try short_id first
      if (/^[a-z0-9]{6}$/i.test(tripIdOrShortId)) {
        try {
          trip = await tripsService.getTripByShortId(tripIdOrShortId);
        } catch (err) {
          // If short_id lookup fails, fall back to full ID lookup
          console.log('Short ID lookup failed, trying full ID:', err);
        }
      }

      // Fall back to full ID lookup if short ID failed or wasn't attempted
      if (!trip) {
        trip = await tripsService.getTripById(tripIdOrShortId);
      }

      setCurrentTrip(trip);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch trip';
      setError(errorMessage);
      console.error('Error fetching trip:', err);
    } finally {
      setLoading(false);
    }
  }, [setCurrentTrip]);

  const createTrip = useCallback(
    async (input: CreateTripInput): Promise<Trip> => {
      if (!firebaseUser) {
        throw new Error('User must be authenticated to create a trip');
      }

      setLoading(true);
      setError(null);

      try {
        const newTrip = await tripsService.createTrip(firebaseUser.uid, input);
        setTrips((prev) => [newTrip, ...prev]);
        return newTrip;
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to create trip';
        setError(errorMessage);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [firebaseUser]
  );

  const updateTrip = useCallback(
    async (tripId: string, input: UpdateTripInput): Promise<Trip> => {
      setLoading(true);
      setError(null);

      try {
        const updatedTrip = await tripsService.updateTrip(tripId, input);
        setTrips((prev) =>
          prev.map((trip) => (trip.id === tripId ? updatedTrip : trip))
        );
        if (currentTripRef.current?.id === tripId) {
          setCurrentTrip(updatedTrip);
        }
        return updatedTrip;
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to update trip';
        setError(errorMessage);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [setCurrentTrip]
  );

  const deleteTrip = useCallback(
    async (tripId: string): Promise<void> => {
      setLoading(true);
      setError(null);

      try {
        await tripsService.deleteTrip(tripId);
        setTrips((prev) => prev.filter((trip) => trip.id !== tripId));
        if (currentTripRef.current?.id === tripId) {
          setCurrentTrip(null);
        }
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to delete trip';
        setError(errorMessage);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [setCurrentTrip]
  );

  return (
    <TripsContext.Provider
      value={{
        trips,
        currentTrip,
        loading,
        error,
        fetchUserTrips,
        fetchTripById,
        createTrip,
        updateTrip,
        deleteTrip,
        setCurrentTrip,
        getLastTripId,
      }}
    >
      {children}
    </TripsContext.Provider>
  );
};

export const useTrips = () => {
  const context = useContext(TripsContext);
  if (context === undefined) {
    throw new Error('useTrips must be used within a TripsProvider');
  }
  return context;
};
